import { dictionaryApi } from './dictionaryClient';
import type { ArthaBidha, ArthaPrakar } from './arthaApi';
import type { Language } from './languageApi';

export type UpasargaArtha = {
  id: number;
  tingantaId: number;
  languageId: number;
  artha: string;
  upasargaIds: number[];
  example?: string | null;
  antonyms?: string | null;
  synonyms?: string | null;
  tags?: string | null;
  bidha?: ArthaBidha | null;
  prakar?: ArthaPrakar | null;
  createdAt?: string;
  updatedAt?: string;
  created_at?: string;
  updated_at?: string;
  language?: Language | null;
};

export type CreateUpasargaArthaBody = {
  tingantaId: number;
  languageId: number;
  artha: string;
  upasargaIds: number[];
  example?: string | null;
  antonyms?: string | null;
  synonyms?: string | null;
  tags?: string | null;
  bidha?: ArthaBidha | null;
  prakar?: ArthaPrakar | null;
};

export type PatchUpasargaArthaBody = Partial<
  Pick<
    UpasargaArtha,
    | 'languageId'
    | 'artha'
    | 'upasargaIds'
    | 'example'
    | 'antonyms'
    | 'synonyms'
    | 'tags'
    | 'bidha'
    | 'prakar'
  >
>;

function normalizeList(data: unknown): UpasargaArtha[] {
  if (Array.isArray(data)) return data as UpasargaArtha[];
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: UpasargaArtha[] }).data;
  }
  return [];
}

function normOpt(s: string | null | undefined): string | null {
  if (s == null) return null;
  const t = String(s).trim();
  return t === '' ? null : t;
}

function normUpasargaIds(raw: unknown): number[] | null {
  if (!Array.isArray(raw)) return null;
  const out: number[] = [];
  for (const x of raw) {
    const n = Number(x);
    if (!Number.isFinite(n)) return null;
    out.push(n);
  }
  return out;
}

export function isUpasargaArtha(x: unknown): x is UpasargaArtha {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  const ids = normUpasargaIds(o.upasargaIds);
  return (
    typeof o.id === 'number' &&
    typeof o.tingantaId === 'number' &&
    typeof o.languageId === 'number' &&
    typeof o.artha === 'string' &&
    ids != null
  );
}

export function parseUpasargaArthas(raw: unknown[] | undefined): UpasargaArtha[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isUpasargaArtha);
}

export async function listUpasargaArthas(tingantaId?: number): Promise<UpasargaArtha[]> {
  const res = await dictionaryApi.get<unknown>('/upasarga-artha', {
    params: tingantaId != null ? { tingantaId } : undefined,
  });
  return normalizeList(res.data);
}

export async function getUpasargaArtha(id: number): Promise<UpasargaArtha> {
  const res = await dictionaryApi.get<UpasargaArtha>(`/upasarga-artha/${id}`);
  return res.data;
}

function createPayload(body: CreateUpasargaArthaBody): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    tingantaId: body.tingantaId,
    languageId: body.languageId,
    artha: (body.artha ?? '').trim(),
    upasargaIds: body.upasargaIds,
  };
  const ex = normOpt(body.example);
  if (ex != null) payload.example = ex;
  const an = normOpt(body.antonyms);
  if (an != null) payload.antonyms = an;
  const sy = normOpt(body.synonyms);
  if (sy != null) payload.synonyms = sy;
  const ta = normOpt(body.tags);
  if (ta != null) payload.tags = ta;
  if (body.bidha != null) payload.bidha = body.bidha;
  if (body.prakar != null) payload.prakar = body.prakar;
  return payload;
}

export async function createUpasargaArtha(body: CreateUpasargaArthaBody): Promise<UpasargaArtha> {
  const res = await dictionaryApi.post<UpasargaArtha>('/upasarga-artha', createPayload(body));
  return res.data;
}

export async function updateUpasargaArtha(
  id: number,
  body: PatchUpasargaArthaBody
): Promise<UpasargaArtha> {
  const res = await dictionaryApi.patch<UpasargaArtha>(`/upasarga-artha/${id}`, body);
  return res.data;
}

export async function deleteUpasargaArtha(id: number): Promise<void> {
  await dictionaryApi.delete(`/upasarga-artha/${id}`);
}

export type UpasargaArthaFormValues = {
  languageId: number;
  artha: string;
  upasargaIds: number[];
  example?: string;
  antonyms?: string;
  synonyms?: string;
  tags?: string;
  bidha?: ArthaBidha | null;
  prakar?: ArthaPrakar | null;
};

export function buildUpasargaArthaPatch(
  prev: UpasargaArtha,
  next: UpasargaArthaFormValues
): PatchUpasargaArthaBody {
  const patch: PatchUpasargaArthaBody = {};
  if (prev.languageId !== next.languageId) patch.languageId = next.languageId;
  const artha = (next.artha ?? '').trim();
  if (prev.artha !== artha) patch.artha = artha;
  const pa = JSON.stringify(prev.upasargaIds ?? []);
  const na = JSON.stringify(next.upasargaIds ?? []);
  if (pa !== na) patch.upasargaIds = [...(next.upasargaIds ?? [])];

  const pe = normOpt(prev.example);
  const ne = normOpt(next.example);
  if (pe !== ne) patch.example = ne;
  const pnt = normOpt(prev.antonyms);
  const nnt = normOpt(next.antonyms);
  if (pnt !== nnt) patch.antonyms = nnt;
  const ps = normOpt(prev.synonyms);
  const ns = normOpt(next.synonyms);
  if (ps !== ns) patch.synonyms = ns;
  const pt = normOpt(prev.tags);
  const nt = normOpt(next.tags);
  if (pt !== nt) patch.tags = nt;

  const nb = next.bidha == null ? null : next.bidha;
  const pb = prev.bidha ?? null;
  if (pb !== nb) patch.bidha = nb;

  const np = next.prakar == null ? null : next.prakar;
  const pp = prev.prakar ?? null;
  if (pp !== np) patch.prakar = np;
  return patch;
}
